import { useCallback, useMemo, useReducer, useState } from "react";
import { useSelector } from "react-redux";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import type {
  ConditionField,
  DrillDownDataRequest,
  MeasureDrillDownInfo,
  MetricDrillDownInfo,
} from "../../../../../api/types/svTypes.ts";
import { CellDrillDownInfoType, MeasureUnitTable } from "../../../../../api/types/svTypes.ts";
import { getDrillDownColumns, getSorting } from "../components/builder/common/drillDown/utils/drillDownHelper";
import { selectDimensions } from "../store/selectors/metaDataSelectors";
import { getDrillDownConditionLeafFields, mapTreeToConditionDescriptors } from "./drillDownConditions.ts";
import { drillDownActions, drillDownReducer, initialDrillDownState } from "./drillDownState";
import { fieldStateActions, fieldStateReducer, initializeDrilldown } from "./fieldsState";
import type { FieldWithOrder, FieldWithSorting } from "./FieldWithOrder";

export default function useDrillDown(info: MeasureDrillDownInfo | MetricDrillDownInfo) {
  const dimensions = useSelector(selectDimensions);
  const [fieldState, fieldStateDispatch] = useReducer(
    fieldStateReducer,
    { conditions: info.conditions, dimensions },
    initializeDrilldown
  );
  const [state, dispatch] = useReducer(drillDownReducer, initialDrillDownState);
  const [cacheSessionId] = useState(generateGuid);

  const conditions = useMemo(
    () => getDrillDownConditionLeafFields(fieldState.conditionTree),
    [fieldState.conditionTree]
  );

  const addCondition = useCallback((field: ConditionField) => {
    fieldStateDispatch(fieldStateActions.addCondition(field));
  }, []);
  const updateCondition = useCallback((field: ConditionField, changes: Partial<ConditionField>) => {
    fieldStateDispatch(fieldStateActions.updateCondition({ field, changes }));
  }, []);
  const removeCondition = useCallback((field: ConditionField) => {
    fieldStateDispatch(fieldStateActions.removeCondition(field));
  }, []);

  const drillDownConfig = useMemo((): DrillDownDataRequest => {
    const columns = getDrillDownColumns(state.selectedFields);
    return info.drillDownInfoType === CellDrillDownInfoType.Metric
      ? {
          columns: columns,
          conditions: mapTreeToConditionDescriptors(fieldState.conditionTree),
          sortingColumns: getSorting(state.sortedFields),
          range: { skip: 0, take: 0, totals: true },
          table: MeasureUnitTable.Gl,
          allocated: false,
          cacheSessionId,
        }
      : {
          columns: columns,
          conditions: mapTreeToConditionDescriptors(fieldState.conditionTree),
          sortingColumns: getSorting(state.sortedFields),
          range: { skip: 0, take: 0, totals: true },
          table: info.table || MeasureUnitTable.Gl,
          allocated: info.allocated === true,
          cacheSessionId,
        };
  }, [state.selectedFields, state.sortedFields, fieldState.conditionTree, info, cacheSessionId]);

  const setTotalRows = useCallback((num: number) => {
    dispatch(drillDownActions.setRowsTotal(num));
  }, []);

  const changeSelectedFields = useCallback((fields: FieldWithOrder[]) => {
    dispatch(drillDownActions.setSelectedFields([...fields]));
  }, []);

  const changeFieldSorting = useCallback((fields: FieldWithSorting[]) => {
    dispatch(drillDownActions.setSortedFields([...fields]));
  }, []);

  const setError = useCallback((error: string | undefined) => {
    dispatch(drillDownActions.setError(error));
  }, []);

  return {
    conditionTree: fieldState.conditionTree,
    conditions,
    ...state,
    drillDownConfig,
    actions: {
      addCondition,
      updateCondition,
      removeCondition,
      changeSelectedFields,
      changeFieldSorting,
      setTotalRows,
      setError,
    },
  };
}

export type DrillDownState = ReturnType<typeof useDrillDown>;
